import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";

import {
  NotificationContext,
  type NotificationItem,
  type NotificationKind,
  type Notifications,
} from "./useNotifications";

interface NotificationProviderProps {
  children: ReactNode;
  autoDismissMs?: number;
}

const KIND_STYLES: Record<NotificationKind, string> = {
  success: "border-emerald-500/70 bg-emerald-950/95 text-emerald-50",
  error: "border-red-500/70 bg-red-950/95 text-red-50",
  info: "border-brand-500/70 bg-stone-950/95 text-stone-50",
};

function makeNotificationId(counter: number): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `notification-${counter}`;
}

function NotificationToast({
  notification,
  autoDismissMs,
  onDismiss,
}: {
  notification: NotificationItem;
  autoDismissMs: number;
  onDismiss: (id: string) => void;
}) {
  const { t } = useTranslation();

  useEffect(() => {
    const timeout = setTimeout(() => onDismiss(notification.id), autoDismissMs);
    return () => clearTimeout(timeout);
  }, [autoDismissMs, notification.id, onDismiss]);

  const liveProps =
    notification.kind === "error"
      ? ({ role: "alert" } as const)
      : ({ role: "status", "aria-live": "polite" } as const);

  return (
    <div
      {...liveProps}
      className={`toast-enter flex items-start justify-between gap-3 rounded-lg border px-4 py-3 text-sm shadow-2xl ${KIND_STYLES[notification.kind]}`}
    >
      <p className="min-w-0 flex-1 leading-5">{notification.message}</p>
      <button
        type="button"
        onClick={() => onDismiss(notification.id)}
        aria-label={t("notifications.close")}
        className="shrink-0 rounded-md border border-current/30 px-2 py-0.5 text-xs font-semibold text-current opacity-80 transition-colors hover:bg-white/10 hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current/50"
      >
        x
      </button>
    </div>
  );
}

export function NotificationProvider({
  children,
  autoDismissMs = 5000,
}: NotificationProviderProps) {
  const { t } = useTranslation();
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const nextIdRef = useRef(0);

  const dismissNotification = useCallback((id: string) => {
    setNotifications((current) =>
      current.filter((notification) => notification.id !== id),
    );
  }, []);

  const notify = useCallback((kind: NotificationKind, message: string): string => {
    nextIdRef.current += 1;
    let id = makeNotificationId(nextIdRef.current);
    setNotifications((current) => {
      const existing = current.find(
        (notification) =>
          notification.kind === kind && notification.message === message,
      );
      if (existing) {
        id = existing.id;
        return current;
      }
      return [...current, { id, kind, message }];
    });
    return id;
  }, []);

  const value = useMemo<Notifications>(
    () => ({
      notifyError: (message) => notify("error", message),
      notifySuccess: (message) => notify("success", message),
      notifyInfo: (message) => notify("info", message),
      dismissNotification,
    }),
    [dismissNotification, notify],
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <div
        aria-label={t("notifications.region")}
        className="pointer-events-none fixed left-1/2 top-4 z-50 flex w-[min(100%-2rem,28rem)] -translate-x-1/2 flex-col gap-2"
      >
        {notifications.map((notification) => (
          <div key={notification.id} className="pointer-events-auto">
            <NotificationToast
              notification={notification}
              autoDismissMs={autoDismissMs}
              onDismiss={dismissNotification}
            />
          </div>
        ))}
      </div>
    </NotificationContext.Provider>
  );
}
